import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { FaBarcode, FaPlus, FaMinus, FaTrash, FaPrint, FaSearch, FaUser, FaWarehouse, FaTimes, FaCamera, FaQrcode, FaCheckCircle, FaSpinner } from 'react-icons/fa';

const API_URL = 'http://localhost:5050';

interface InventoryItemType {
  id: number;
  sku: string;
  name: string;
  description: string;
  price: number;
  current_stock: number;
  minimum_stock_level: number;
  packaging_category_id?: number | null;
  units_per_package?: number;
  package_unit?: string;
  sku_abbreviation?: string | null;
  sku_type?: string | null;
  barcode?: string | null;
  batch_code?: string | null;
}

interface WarehouseType {
  id: number;
  name: string;
  location: string;
}

interface CustomerType {
  id: number;
  name: string;
  email: string;
  phone?: string;
}

interface CartItemType {
  item: InventoryItemType;
  quantity: number;
  unitRate: number;
}

export default function PosTerminal() {
  const [role, setRole] = useState<string | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  
  // Lists
  const [products, setProducts] = useState<InventoryItemType[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseType[]>([]);
  const [customers, setCustomers] = useState<CustomerType[]>([]);

  // Selection states
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string>('');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [paymentMode, setPaymentMode] = useState<string>('Cash');

  // Search & Cart
  const [searchTerm, setSearchTerm] = useState('');
  const [barcodeInput, setBarcodeInput] = useState('');
  const [cart, setCart] = useState<CartItemType[]>([]);
  
  // Camera/Scanner UI State
  const [cameraActive, setCameraActive] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Modals
  const [showWarehouseModal, setShowWarehouseModal] = useState(false);
  const [newWarehouse, setNewWarehouse] = useState({ name: '', location: '' });

  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: '', phone: '', email: '' });

  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [lastCreatedInvoice, setLastCreatedInvoice] = useState<any>(null);

  // UPI payment gateway modal
  const [showUpiModal, setShowUpiModal] = useState(false);
  const [upiPaymentStatus, setUpiPaymentStatus] = useState<'pending' | 'checking' | 'success' | 'failed'>('pending');
  const [upiTimer, setUpiTimer] = useState<number>(60);
  const [simulatedTxnRef, setSimulatedTxnRef] = useState<string>('');

  const barcodeInputRef = useRef<HTMLInputElement>(null);

  // Decode JWT Token
  const decodeToken = (token: string) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(window.atob(base64));
    } catch (e) {
      return {};
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const activeWorkspaceId = localStorage.getItem("selected_company_id");
    if (token) {
      const decoded = decodeToken(token);
      setRole(decoded.role || null);
      const companyId = decoded.userId || decoded.id || decoded.company_id;
      if (decoded.role === 'company' && companyId) {
        setSelectedCompanyId(String(companyId));
      } else if (activeWorkspaceId && activeWorkspaceId !== "all") {
        setSelectedCompanyId(String(activeWorkspaceId));
      }
    } else if (activeWorkspaceId && activeWorkspaceId !== "all") {
      setSelectedCompanyId(String(activeWorkspaceId));
    }
  }, []);

  useEffect(() => {
    if (selectedCompanyId) {
      fetchProducts();
      fetchWarehouses();
      fetchCustomers();
    }
  }, [selectedCompanyId]);

  // UPI Countdown timer
  useEffect(() => {
    let interval: any;
    if (showUpiModal && upiTimer > 0 && upiPaymentStatus === 'pending') {
      interval = setInterval(() => {
        setUpiTimer(prev => prev - 1);
      }, 1000);
    } else if (upiTimer === 0) {
      setUpiPaymentStatus('failed');
    }
    return () => clearInterval(interval);
  }, [showUpiModal, upiTimer, upiPaymentStatus]);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/inventory/items?company_id=${selectedCompanyId}`);
      setProducts(res.data || []);
    } catch (err) {
      toast.error('Failed to load products');
    }
  };

  const fetchWarehouses = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/inventory/warehouses?company_id=${selectedCompanyId}`);
      const list = res.data || [];
      setWarehouses(list);
      if (list.length > 0 && !selectedWarehouseId) {
        setSelectedWarehouseId(String(list[0].id));
      }
    } catch (err) {
      toast.error('Failed to load warehouses');
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/customers?company_id=${selectedCompanyId}`);
      const list = res.data || [];
      const mapped = list.map((c: any) => ({
        id: c.id,
        name: c.company_name,
        email: c.email,
        phone: c.code || ''
      }));
      setCustomers(mapped);
      const generic = mapped.find((c: any) => c.name.toLowerCase().includes('walk-in') || c.name.toLowerCase().includes('cash'));
      if (generic) {
        setSelectedCustomerId(String(generic.id));
      } else if (mapped.length > 0) {
        setSelectedCustomerId(String(mapped[0].id));
      }
    } catch (err) {
      toast.error('Failed to load customers');
    }
  };

  // Add Warehouse
  const handleAddWarehouseSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newWarehouse.name.trim()) return;
    try {
      const res = await axios.post(`${API_URL}/auth/inventory/warehouse`, {
        name: newWarehouse.name,
        location: newWarehouse.location,
        company_id: Number(selectedCompanyId)
      });
      toast.success('Warehouse created successfully!');
      setNewWarehouse({ name: '', location: '' });
      setShowWarehouseModal(false);
      fetchWarehouses();
      if (res.data && res.data.id) {
        setSelectedWarehouseId(String(res.data.id));
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to add warehouse');
    }
  };

  // Add Customer Profile Directly
  const handleAddCustomerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCustomer.name.trim()) return;
    try {
      const res = await axios.post(`${API_URL}/auth/customer`, {
        company_name: newCustomer.name,
        code: newCustomer.phone,
        email: newCustomer.email || null,
        company_id: Number(selectedCompanyId)
      });
      toast.success('Customer profile created successfully!');
      setNewCustomer({ name: '', phone: '', email: '' });
      setShowCustomerModal(false);
      
      // Reload and auto select new customer
      const resCust = await axios.get(`${API_URL}/auth/customers?company_id=${selectedCompanyId}`);
      const list = resCust.data || [];
      const mapped = list.map((c: any) => ({
        id: c.id,
        name: c.company_name,
        email: c.email,
        phone: c.code || ''
      }));
      setCustomers(mapped);
      const newlyAdded = mapped.find((c: any) => c.name === newCustomer.name);
      if (newlyAdded) {
        setSelectedCustomerId(String(newlyAdded.id));
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to add customer profile');
    }
  };

  // Cart operations
  const addToCart = (product: InventoryItemType) => {
    const existing = cart.find(c => c.item.id === product.id);
    if (existing) {
      setCart(cart.map(c => c.item.id === product.id ? { ...c, quantity: c.quantity + 1 } : c));
    } else {
      setCart([...cart, { item: product, quantity: 1, unitRate: product.price || 0 }]);
    }
    toast.success(`${product.name} added to cart`, { duration: 800 });
  };

  const updateCartQty = (productId: number, delta: number) => {
    setCart(cart.map(c => {
      if (c.item.id === productId) {
        const nextQty = c.quantity + delta;
        return nextQty > 0 ? { ...c, quantity: nextQty } : c;
      }
      return c;
    }));
  };

  const removeFromCart = (productId: number) => {
    setCart(cart.filter(c => c.item.id !== productId));
    toast.error('Item removed from cart', { duration: 800 });
  };

  // Handle barcode scanning input
  const handleBarcodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!barcodeInput.trim()) return;
    
    const matched = products.find(p => 
      p.barcode === barcodeInput.trim() || 
      p.sku.toLowerCase() === barcodeInput.trim().toLowerCase()
    );

    if (matched) {
      addToCart(matched);
      setBarcodeInput('');
    } else {
      toast.error(`No product found with code "${barcodeInput}"`);
    }
  };

  // Live Camera Scanner actions
  const startCamera = async () => {
    try {
      setCameraActive(true);
      const constraints = { video: { facingMode: 'environment' } };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      toast.error('Camera access blocked. Using premium scanner simulator.');
    }
  };

  const stopCamera = () => {
    setCameraActive(false);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const simulateScan = () => {
    if (products.length === 0) {
      toast.error('No catalog products to scan.');
      return;
    }
    const rand = products[Math.floor(Math.random() * products.length)];
    addToCart(rand);
  };

  // Calculations
  const subtotal = cart.reduce((acc, c) => acc + (c.quantity * (c.unitRate || 0)), 0);
  const gstAmount = subtotal * 0.18; 
  const grandTotal = subtotal + gstAmount;

  // Checkout process
  const handleCheckoutTrigger = () => {
    if (cart.length === 0) {
      toast.error('Cart is empty!');
      return;
    }
    if (!selectedWarehouseId) {
      toast.error('Please select a warehouse!');
      return;
    }
    if (!selectedCustomerId) {
      toast.error('Please select a customer!');
      return;
    }

    if (paymentMode === 'UPI') {
      setUpiTimer(60);
      setUpiPaymentStatus('pending');
      setSimulatedTxnRef(`TXN${Math.floor(100000000 + Math.random() * 900000000)}`);
      setShowUpiModal(true);
    } else {
      executeInvoiceCreation();
    }
  };

  const executeInvoiceCreation = async () => {
    try {
      const particularsArray = cart.map(c => ({
        desc: c.item.name,
        hsn: c.item.batch_code || c.item.sku,
        qty: c.quantity,
        rate: c.unitRate || 0,
        amount: c.quantity * (c.unitRate || 0),
        gstRate: 18,
        isItem: true,
        itemId: c.item.id,
        warehouseId: Number(selectedWarehouseId)
      }));

      const payload = {
        Company_id: Number(selectedCompanyId),
        Customer_id: Number(selectedCustomerId),
        SGSTAmount: gstAmount / 2,
        CGSTAmount: gstAmount / 2,
        IGSTAmount: 0,
        InvoiceNo: `POS-${Date.now().toString().slice(-6)}`,
        Date: new Date().toISOString().slice(0, 10),
        Particulars: JSON.stringify(particularsArray),
        HSN_SAC: '9983',
        Total_Amount: grandTotal,
        remarks: `POS Terminal checkout - Mode: ${paymentMode}`,
        Amounts: grandTotal,
        invoice_type: 'tax',
        gstIncluded: 0
      };

      const res = await axios.post(`${API_URL}/auth/invoice`, payload);
      
      setLastCreatedInvoice({
        ...payload,
        id: res.data.id || 'N/A',
        cartItems: cart,
        customerName: customers.find(c => String(c.id) === selectedCustomerId)?.name || 'Guest'
      });

      setCart([]);
      fetchProducts();
      setShowReceiptModal(true);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Checkout failed');
    }
  };

  // Simulate gateway confirmation
  const checkUpiPaymentStatus = () => {
    setUpiPaymentStatus('checking');
    setTimeout(() => {
      setUpiPaymentStatus('success');
      toast.success('UPI Payment Confirmed by bank!');
      setTimeout(() => {
        setShowUpiModal(false);
        executeInvoiceCreation();
      }, 1000);
    }, 2000);
  };

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.barcode && p.barcode.includes(searchTerm))
  );

  const upiPayLink = `upi://pay?pa=abbiee@okaxis&pn=Abbiee%20Services&am=${grandTotal.toFixed(2)}&cu=INR&tn=${simulatedTxnRef}`;

  return (
    <div className="flex flex-col lg:flex-row h-[calc(100vh-100px)] gap-6 p-2 bg-slate-50">
      
      {/* Left Column: Product Selection Grid */}
      <div className="flex-1 flex flex-col bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden p-6 space-y-4">
        
        {/* Top search, filter, and camera scanner controls */}
        <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center justify-between border-b pb-4">
          <div className="flex-1 relative">
            <FaSearch className="absolute left-4 top-3.5 text-slate-400" />
            <input
              type="text"
              placeholder="Search product name, SKU or barcode..."
              className="w-full pl-11 pr-4 py-2.5 rounded-xl border border-slate-200 focus:border-indigo-400 focus:outline-none text-sm font-semibold"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2">
            <form onSubmit={handleBarcodeSubmit} className="flex items-center gap-2">
              <div className="relative">
                <FaBarcode className="absolute left-4 top-3.5 text-slate-500" />
                <input
                  ref={barcodeInputRef}
                  type="text"
                  placeholder="Scan code..."
                  className="w-36 pl-11 pr-4 py-2.5 rounded-xl border-2 border-indigo-200 focus:border-indigo-500 focus:outline-none text-xs font-bold font-mono bg-indigo-50/50"
                  value={barcodeInput}
                  onChange={(e) => setBarcodeInput(e.target.value)}
                />
              </div>
              <button
                type="submit"
                className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold px-3 py-2.5 rounded-xl text-xs transition shadow"
              >
                Add
              </button>
            </form>

            {cameraActive ? (
              <button
                type="button"
                onClick={stopCamera}
                className="bg-slate-800 hover:bg-slate-700 text-white font-bold px-3 py-2.5 rounded-xl text-xs flex items-center gap-1"
              >
                <FaTimes /> Stop Cam
              </button>
            ) : (
              <button
                type="button"
                onClick={startCamera}
                className="bg-indigo-50 border border-indigo-200 text-indigo-700 font-bold px-3 py-2.5 rounded-xl text-xs flex items-center gap-1.5 hover:bg-indigo-100"
              >
                <FaCamera /> POS Scanner
              </button>
            )}
          </div>
        </div>

        {/* Dynamic camera scanner component view */}
        {cameraActive && (
          <div className="bg-slate-900 aspect-video max-h-48 rounded-2xl overflow-hidden relative flex items-center justify-center text-white border-2 border-indigo-300">
            <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover"></video>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-36 h-20 border-2 border-dashed border-amber-300 rounded-lg flex items-center justify-center bg-black/30 backdrop-blur-2xs text-[9px] text-amber-200 uppercase font-extrabold tracking-wider">
                Align Product Barcode
              </div>
            </div>
            <button
              type="button"
              onClick={simulateScan}
              className="absolute bottom-2 right-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold px-3 py-1.5 rounded-lg text-xs"
            >
              Simulate Scan Capture
            </button>
          </div>
        )}

        {/* Product Cards Grid */}
        <div className="flex-1 overflow-y-auto grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 pr-2">
          {filteredProducts.map(p => {
            const lowStock = p.current_stock <= p.minimum_stock_level;
            return (
              <div
                key={p.id}
                onClick={() => addToCart(p)}
                className={`group border rounded-2xl p-4 flex flex-col justify-between cursor-pointer hover:border-indigo-400 hover:shadow-md transition bg-white relative ${lowStock ? 'border-rose-100 bg-rose-50/10' : 'border-slate-100'}`}
              >
                <div>
                  <div className="flex justify-between items-start gap-1">
                    <span className="text-[10px] font-mono text-indigo-600 font-bold tracking-wider truncate uppercase max-w-[70px]">
                      {p.sku}
                    </span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded font-extrabold uppercase border ${lowStock ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
                      {p.current_stock} pcs
                    </span>
                  </div>
                  <h4 className="font-extrabold text-slate-800 text-sm mt-1.5 group-hover:text-indigo-600 transition truncate">
                    {p.name}
                  </h4>
                  <p className="text-[11px] text-slate-400 mt-1 line-clamp-2 h-7 leading-relaxed">
                    {p.description || 'No description available'}
                  </p>
                </div>

                <div className="flex justify-between items-center mt-3 pt-2 border-t border-slate-50">
                  <span className="font-extrabold text-sm text-slate-900">
                    ₹ {Number(p.price || 0).toFixed(2)}
                  </span>
                  <span className="text-[10px] text-indigo-500 font-extrabold opacity-0 group-hover:opacity-100 transition-all">
                    + Add to Cart
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Right Column: Checkout Cart Pane */}
      <div className="w-full lg:w-[420px] flex flex-col bg-white rounded-3xl border border-slate-100 shadow-lg overflow-hidden">
        
        {/* Cart Config Header */}
        <div className="p-5 border-b space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-extrabold text-slate-800 text-lg flex items-center gap-2">
              🛒 Current Order
            </h3>
            <span className="bg-indigo-50 border border-indigo-100 text-indigo-700 font-extrabold text-xs px-2.5 py-1 rounded-lg">
              {cart.reduce((sum, c) => sum + c.quantity, 0)} Items
            </span>
          </div>

          {/* Warehouse selector */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <label className="text-[10px] font-bold text-slate-500 uppercase flex items-center gap-1">
                <FaWarehouse /> Warehouse Store
              </label>
              <button
                type="button"
                onClick={() => setShowWarehouseModal(true)}
                className="text-[10px] text-indigo-600 font-extrabold hover:underline"
              >
                + Add Warehouse
              </button>
            </div>
            <select
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:outline-none focus:border-indigo-400 bg-slate-50/50"
              value={selectedWarehouseId}
              onChange={(e) => setSelectedWarehouseId(e.target.value)}
            >
              <option value="">-- Choose Warehouse --</option>
              {warehouses.map(w => (
                <option key={w.id} value={w.id}>{w.name} ({w.location || 'Local'})</option>
              ))}
            </select>
          </div>

          {/* Customer profile selection & quick create */}
          <div className="space-y-1">
            <div className="flex justify-between items-center">
              <label className="text-[10px] font-bold text-slate-500 uppercase flex items-center gap-1">
                <FaUser /> Customer Profile
              </label>
              <button
                type="button"
                onClick={() => setShowCustomerModal(true)}
                className="text-[10px] text-indigo-600 font-extrabold hover:underline"
              >
                + New Customer
              </button>
            </div>
            <select
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:outline-none focus:border-indigo-400 bg-slate-50/50"
              value={selectedCustomerId}
              onChange={(e) => setSelectedCustomerId(e.target.value)}
            >
              {customers.map(c => (
                <option key={c.id} value={c.id}>{c.name} {c.phone ? `(${c.phone})` : ''}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Cart items listing */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-slate-50/30">
          {cart.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-8 space-y-2">
              <span className="text-4xl text-slate-300">🛍️</span>
              <p className="text-slate-400 font-semibold text-sm">Your order card is empty</p>
              <p className="text-[10px] text-slate-400">Click products from the grid or scan barcodes to begin checkout</p>
            </div>
          ) : (
            cart.map(c => (
              <div key={c.item.id} className="bg-white border border-slate-100 p-3 rounded-2xl flex justify-between items-center shadow-xs">
                <div className="flex-1 truncate pr-2">
                  <h5 className="font-bold text-slate-800 text-xs truncate">{c.item.name}</h5>
                  <span className="text-[9px] font-mono text-slate-400 block mt-0.5">₹ {Number(c.unitRate || 0).toFixed(2)} each</span>
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex items-center border rounded-lg overflow-hidden bg-slate-50">
                    <button
                      type="button"
                      onClick={() => updateCartQty(c.item.id, -1)}
                      className="px-2 py-1 text-xs text-slate-600 hover:bg-slate-200"
                    >
                      <FaMinus className="text-[8px]" />
                    </button>
                    <span className="px-2 text-xs font-bold text-slate-800 min-w-[20px] text-center">{c.quantity}</span>
                    <button
                      type="button"
                      onClick={() => updateCartQty(c.item.id, 1)}
                      className="px-2 py-1 text-xs text-slate-600 hover:bg-slate-200"
                    >
                      <FaPlus className="text-[8px]" />
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={() => removeFromCart(c.item.id)}
                    className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-lg transition"
                  >
                    <FaTrash className="text-xs" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Calculations footer & submit action */}
        <div className="p-5 border-t bg-white space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase">Payment Mode</span>
            <div className="flex gap-1.5">
              {['Cash', 'Card', 'UPI'].map(mode => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setPaymentMode(mode)}
                  className={`px-3 py-1 rounded-lg text-xs font-bold transition border ${paymentMode === mode ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-slate-50 text-slate-600 border-slate-200'}`}
                >
                  {mode}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5 pt-2 border-t text-xs">
            <div className="flex justify-between text-slate-600">
              <span>Subtotal</span>
              <span className="font-bold">₹ {subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-slate-600">
              <span>GST (18%)</span>
              <span className="font-bold">₹ {gstAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-slate-900 font-extrabold text-base pt-1 border-t">
              <span>Payable Total</span>
              <span>₹ {grandTotal.toFixed(2)}</span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleCheckoutTrigger}
            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold py-3.5 rounded-2xl text-sm transition shadow-lg flex items-center justify-center gap-2 mt-2"
          >
            Confirm & Print Receipt
          </button>
        </div>
      </div>

      {/* Warehouse Modal */}
      {showWarehouseModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50">
          <div className="bg-white w-full max-w-sm rounded-3xl shadow-2xl p-6 space-y-4">
            <div className="flex justify-between items-center border-b pb-2">
              <h3 className="font-extrabold text-slate-800 text-base">Create Warehouse</h3>
              <button onClick={() => setShowWarehouseModal(false)} className="text-slate-400 hover:text-slate-600">
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddWarehouseSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Warehouse Name</label>
                <input
                  type="text"
                  placeholder="e.g. Central Hub"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold focus:outline-none focus:border-indigo-400"
                  value={newWarehouse.name}
                  onChange={(e) => setNewWarehouse({ ...newWarehouse, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Location Details</label>
                <input
                  type="text"
                  placeholder="e.g. Floor 2, Building B"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold focus:outline-none focus:border-indigo-400"
                  value={newWarehouse.location}
                  onChange={(e) => setNewWarehouse({ ...newWarehouse, location: e.target.value })}
                />
              </div>
              <button
                type="submit"
                className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-2.5 rounded-xl text-xs transition"
              >
                Create Warehouse
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Customer Modal */}
      {showCustomerModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50">
          <div className="bg-white w-full max-w-sm rounded-3xl shadow-2xl p-6 space-y-4">
            <div className="flex justify-between items-center border-b pb-2">
              <h3 className="font-extrabold text-slate-800 text-base">New Customer Profile</h3>
              <button onClick={() => setShowCustomerModal(false)} className="text-slate-400 hover:text-slate-600">
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddCustomerSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Customer / Business Name</label>
                <input
                  type="text"
                  placeholder="e.g. Ashish Bhardwaj"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold focus:outline-none focus:border-indigo-400"
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Mobile / Phone Number</label>
                <input
                  type="text"
                  placeholder="e.g. +91 9988776655"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold focus:outline-none focus:border-indigo-400"
                  value={newCustomer.phone}
                  onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Email Address (Optional)</label>
                <input
                  type="email"
                  placeholder="e.g. customer@example.com"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold focus:outline-none focus:border-indigo-400"
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                />
              </div>
              <button
                type="submit"
                className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-2.5 rounded-xl text-xs transition"
              >
                Save Customer Profile
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Payment Gateway / UPI QR Code Modal */}
      {showUpiModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50">
          <div className="bg-white w-full max-w-sm rounded-3xl shadow-2xl p-6 text-center space-y-5">
            <div className="flex justify-between items-center border-b pb-2">
              <h3 className="font-extrabold text-slate-800 text-base flex items-center gap-1.5">
                <FaQrcode className="text-indigo-600" /> UPI QR Gateway
              </h3>
              <button onClick={() => setShowUpiModal(false)} className="text-slate-400 hover:text-slate-600">
                <FaTimes />
              </button>
            </div>

            <div className="space-y-1">
              <span className="text-xs text-slate-400 uppercase font-bold tracking-wider">Amount to Pay</span>
              <h2 className="text-3xl font-extrabold text-indigo-950">₹ {grandTotal.toFixed(2)}</h2>
            </div>

            <div className="flex justify-center p-3 bg-slate-50 border border-slate-100 rounded-2xl">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(upiPayLink)}`}
                alt="UPI Payment QR Code"
                className="w-44 h-44 shadow-xs"
              />
            </div>

            <div className="space-y-4">
              {upiPaymentStatus === 'pending' && (
                <div className="space-y-3">
                  <p className="text-xs text-slate-600 font-bold flex items-center justify-center gap-1.5">
                    <span className="w-2.5 h-2.5 bg-amber-500 rounded-full animate-ping"></span>
                    Scan QR code with your UPI App. Expires in <span className="text-rose-500 font-extrabold">{upiTimer}s</span>
                  </p>
                  <p className="text-[10px] text-slate-400 font-mono">Ref Ref: {simulatedTxnRef}</p>
                  
                  <button
                    type="button"
                    onClick={checkUpiPaymentStatus}
                    className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-extrabold py-2.5 rounded-xl text-xs transition shadow flex items-center justify-center gap-2"
                  >
                    Check Transaction Status
                  </button>
                </div>
              )}

              {upiPaymentStatus === 'checking' && (
                <div className="flex flex-col items-center justify-center py-4 space-y-2">
                  <FaSpinner className="animate-spin text-2xl text-indigo-600" />
                  <p className="text-xs font-semibold text-slate-600">Reconciling ledger entries with NPCI bank rails...</p>
                </div>
              )}

              {upiPaymentStatus === 'success' && (
                <div className="flex flex-col items-center justify-center py-4 space-y-2 text-emerald-600">
                  <FaCheckCircle className="text-3xl animate-bounce" />
                  <p className="text-xs font-extrabold uppercase">Transaction Authorized Successfully!</p>
                </div>
              )}

              {upiPaymentStatus === 'failed' && (
                <div className="space-y-2">
                  <p className="text-xs text-rose-600 font-bold">Transaction timeout. Payment confirmation failed.</p>
                  <button
                    type="button"
                    onClick={() => {
                      setUpiTimer(60);
                      setUpiPaymentStatus('pending');
                    }}
                    className="bg-indigo-50 text-indigo-600 border border-indigo-200 px-4 py-2 rounded-xl text-xs font-bold hover:bg-indigo-100 transition"
                  >
                    Retry Payment
                  </button>
                </div>
              )}
            </div>

          </div>
        </div>
      )}

      {/* POS Receipt thermal Print Modal */}
      {showReceiptModal && lastCreatedInvoice && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50">
          <div className="bg-white w-full max-w-xs rounded-3xl shadow-2xl p-6 flex flex-col items-center">
            
            {/* Thermal Receipt Print Area */}
            <div id="pos-thermal-receipt" className="w-full text-slate-900 font-mono text-[10px] p-2 space-y-3 bg-white border border-slate-200">
              <div className="text-center font-bold">
                <p className="text-xs uppercase">ABBIEE PROJECTS POS</p>
                <p>Invoice No: {lastCreatedInvoice.InvoiceNo}</p>
                <p>Date: {lastCreatedInvoice.Date}</p>
              </div>

              <div className="border-t border-b border-dashed py-1.5">
                <p>Client: {lastCreatedInvoice.customerName}</p>
                <p>Payment: {lastCreatedInvoice.remarks}</p>
              </div>

              <table className="w-full">
                <thead>
                  <tr className="border-b font-bold text-left">
                    <th>Item</th>
                    <th className="text-center">Qty</th>
                    <th className="text-right">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {lastCreatedInvoice.cartItems.map((c: CartItemType) => (
                    <tr key={c.item.id}>
                      <td className="truncate max-w-[100px]">{c.item.name}</td>
                      <td className="text-center">{c.quantity}</td>
                      <td className="text-right">₹ {(c.quantity * (c.unitRate || 0)).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="border-t border-dashed pt-2 text-right space-y-1">
                <p>Subtotal: ₹ {subtotal.toFixed(2)}</p>
                <p>GST (18%): ₹ {gstAmount.toFixed(2)}</p>
                <p className="font-extrabold text-xs">Total: ₹ {grandTotal.toFixed(2)}</p>
              </div>

              <div className="text-center text-[8px] pt-4 text-slate-400">
                <p>Thank you for shopping with us!</p>
                <p>Visit again!</p>
              </div>
            </div>

            <div className="flex gap-3 w-full mt-5">
              <button
                type="button"
                onClick={() => {
                  const printContents = document.getElementById('pos-thermal-receipt')?.innerHTML;
                  const originalContents = document.body.innerHTML;
                  if (printContents) {
                    document.body.innerHTML = printContents;
                    window.print();
                    document.body.innerHTML = originalContents;
                    window.location.reload(); 
                  }
                }}
                className="flex-1 bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-2.5 rounded-xl text-xs flex items-center justify-center gap-1.5 transition"
              >
                <FaPrint /> Print Receipt
              </button>
              <button
                type="button"
                onClick={() => setShowReceiptModal(false)}
                className="flex-1 border border-slate-200 text-slate-700 font-bold py-2.5 rounded-xl text-xs hover:bg-slate-50 transition"
              >
                Close
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}
